import { Component, EventEmitter, Input, Output } from '@angular/core';
import { HttpService } from 'src/app/http.service';

// Inline approve/reject of THIS player's pending deposit & withdraw requests.
// Mirrors the company-wide Deposit / Withdraw Management screens but scoped to
// one player, so an operator never has to navigate away from the player page.
// Approve/reject reuse the SAME backend routes (/admin/deposit/update,
// /admin/withdraw/update) which carry the idempotency + refund logic.
@Component({
  selector: 'app-player-dw-approval',
  templateUrl: './player-dw-approval.component.html',
  styleUrls: ['./player-dw-approval.component.scss']
})
export class PlayerDwApprovalComponent {

  @Input() setPlayer: any;
  @Output() closeEvent = new EventEmitter<any>();
  @Output() updatedEvent = new EventEmitter<any>();

  public deposits: any = [];
  public withdraws: any = [];
  public banks: any = [];
  public is_loading: boolean = false;
  public allow_edit_balance: boolean = false;
  public trim_step: number = 1;

  constructor(private http: HttpService) { }

  ngOnInit() {
    this.getSetting();
    this.getBanks();
    this.load();
  }

  public async getSetting() {
    let rtn: any = await this.http.post("/admin/company/setting");
    this.allow_edit_balance = rtn && rtn.setting && rtn.setting.allow_edit_withdraw_balance === true;
    let step = rtn && rtn.setting ? parseFloat(rtn.setting.withdraw_trim_step) : 1;
    this.trim_step = (isNaN(step) || step <= 0) ? 1 : step;
  }

  public async getBanks() {
    let rtn: any = await this.http.post("/admin/bank/get");
    this.banks = rtn.banks;
  }

  public async load() {
    if (!this.setPlayer || !this.setPlayer.uuid) { return; }
    this.is_loading = true;
    let rtn: any = await this.http.post("/admin/player/pending-dw", { player_uuid: this.setPlayer.uuid });
    this.is_loading = false;
    if (!rtn || !rtn.status) {
      this.http.toast((rtn && rtn.message) || "Failed to load requests", "error");
      return;
    }
    this.deposits = (rtn.deposits || []).map((d: any) => {
      d.status = 1;            // default action = Approve
      d.bank_id = this.banks && this.banks.length ? this.banks[0].id : null;
      return d;
    });
    this.withdraws = (rtn.withdraws || []).map((w: any) => {
      w.status = 1;            // default action = Approve
      w.max_amount = parseFloat(w.amount);
      return w;
    });
  }

  // ---- Deposit ----
  async updateDeposit(deposit: any) {
    if (deposit._submitting) { return; }

    if (deposit.status == 1 && !deposit.bank_id) {
      this.http.toast("Please select bank.", "error");
      return;
    }

    // Same promotion guard the Deposit Management screen applies before crediting.
    let check: any = await this.http.post("/admin/promotion/validate-deposit", { player_uuid: deposit.player_uuid });
    if (!check.status) {
      this.http.toast("There are active promotions and existing balance will be forfeited. You are required to cancel the promotion first.", "error");
      return;
    }

    deposit._submitting = true;
    let rtn: any;
    try {
      rtn = await this.http.post("/admin/deposit/update", deposit);
    } finally {
      deposit._submitting = false;
    }

    if (rtn && rtn.status) {
      this.http.toast(deposit.status == 1 ? "Deposit approved." : "Deposit rejected.", "success");
      this.updatedEvent.emit(true);
      this.load();
    }
    if (rtn && rtn.message) {
      this.http.toast(rtn.message, "error");
    }
  }

  // ---- Withdraw ----
  trimAmount(withdraw: any) {
    let v = parseFloat(withdraw.amount);
    if (isNaN(v) || v <= 0) { withdraw.amount = 0; return; }
    let s = parseFloat(this.trim_step as any);
    if (isNaN(s) || s <= 0) { s = 1; }
    let result = Math.floor(v / s) * s;
    if (result >= v - 1e-9) { result -= s; }
    result = Math.round((result + Number.EPSILON) * 100) / 100;
    if (result < 0) { result = 0; }
    withdraw.amount = result;
  }

  clampAmount(withdraw: any) {
    let v = parseFloat(withdraw.amount);
    if (isNaN(v) || v < 0) { v = 0; }
    if (v > withdraw.max_amount) { v = withdraw.max_amount; }
    withdraw.amount = v;
  }

  changeBank(withdraw: any) {
    let bank = this.banks.find((b: any) => b.id == withdraw.bank_id);
    withdraw.is_auto = bank ? bank.auto : false;
  }

  async uploadFile(event: any, withdraw: any) {
    let base64 = await this.http.fileChangeEvent(event);
    withdraw.file = base64;
  }

  async updateWithdraw(withdraw: any) {
    if (withdraw._submitting) { return; }

    if (withdraw.status == 1 && !withdraw.bank_id) {
      this.http.toast("Please select bank.", "error");
      return;
    }

    if (withdraw.status == 1 && withdraw.bank_id && !withdraw.is_auto && !withdraw.file) {
      this.http.toast("Please upload proof of payment.", "error");
      return;
    }

    if (this.allow_edit_balance && withdraw.status == 1) {
      let amount = parseFloat(withdraw.amount);
      if (isNaN(amount) || amount <= 0) {
        this.http.toast("Withdraw amount must be greater than 0.", "error");
        return;
      }
      if (amount > withdraw.max_amount) {
        this.http.toast("Withdraw amount cannot exceed the requested amount.", "error");
        return;
      }
    }

    withdraw._submitting = true;
    let rtn: any;
    try {
      rtn = await this.http.post("/admin/withdraw/update", withdraw);
    } finally {
      withdraw._submitting = false;
    }

    if (rtn && rtn.status) {
      this.http.toast(withdraw.status == 1 ? "Withdraw approved." : "Withdraw rejected.", "success");
      this.updatedEvent.emit(true);
      this.load();
    }
    if (rtn && rtn.message) {
      this.http.toast(rtn.message, "error");
    }
  }

  close() {
    this.closeEvent.emit(true);
  }
}
